import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Funnel, MoreHorizontal } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { AnnouncerProvider, useAnnounce } from '@/shared/providers/announcer-provider'
import { Popover, PopoverTrigger } from '@/ui/popover'

type ResultStatus = {
  count: number | null
  hasMore?: boolean
  singular: string
  plural: string
  changeKey: string
}

export function Th({
  className,
  children,
  resizer,
  ...props
}: ComponentProps<'th'> & { resizer?: ReactNode }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-border text-muted-foreground border-r px-3 py-2 font-medium last:border-r-0',
        className,
      )}
      {...props}
    >
      {children}
      {resizer}
    </th>
  )
}

export function SortableTh({
  className,
  label,
  direction,
  onSort,
  filterContent,
  filterActive = false,
  onFilterOpenChange,
  resizer,
  ...props
}: Omit<ComponentProps<'th'>, 'aria-sort' | 'children'> & {
  label: string
  direction?: 'asc' | 'desc'
  onSort?: () => void
  filterContent?: (open: boolean) => ReactNode
  filterActive?: boolean
  onFilterOpenChange?: (open: boolean) => void
  resizer?: ReactNode
}) {
  const [filterOpen, setFilterOpen] = useState(false)
  const hasActiveControl = direction !== undefined || filterActive
  const SortIcon = direction === 'asc'
    ? ArrowUp
    : direction === 'desc'
      ? ArrowDown
      : ArrowUpDown

  return (
    <Th
      aria-label={label}
      aria-sort={onSort
        ? direction === undefined
          ? 'none'
          : direction === 'asc'
            ? 'ascending'
            : 'descending'
        : undefined}
      className={cn('group/header relative', className)}
      resizer={resizer}
      {...props}
    >
      {filterContent ? (
        <span className="flex w-full items-center justify-between gap-2">
          <span className="min-w-0 truncate">{label}</span>
          <span className={cn('relative h-5 shrink-0', onSort ? 'w-10' : 'w-5')}>
            <span className={cn(
              'absolute inset-0 flex items-center justify-end transition-all duration-150 ease-out group-focus-within/header:translate-x-1 group-focus-within/header:opacity-0 group-hover/header:translate-x-1 group-hover/header:opacity-0 motion-reduce:transition-none',
              hasActiveControl && 'hidden',
            )}>
              <MoreHorizontal
                aria-hidden
                data-slot="column-header-more"
                className="text-muted-foreground/60 size-3.5"
              />
            </span>
            <span
              data-slot="column-header-actions"
              className={cn(
                'absolute inset-0 flex items-center motion-reduce:transition-none',
                hasActiveControl
                  ? 'pointer-events-auto translate-x-0 opacity-100'
                  : 'pointer-events-none -translate-x-1 opacity-0 transition-all duration-150 ease-out group-focus-within/header:pointer-events-auto group-focus-within/header:translate-x-0 group-focus-within/header:opacity-100 group-hover/header:pointer-events-auto group-hover/header:translate-x-0 group-hover/header:opacity-100',
              )}
            >
              {onSort && (
                <button
                  type="button"
                  onClick={onSort}
                  aria-label={`Sort ${label}`}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/30 flex size-5 items-center justify-center rounded-sm outline-hidden focus-visible:ring-2"
                >
                  <SortIcon
                    aria-hidden
                    className={cn('size-3', direction && 'text-foreground')}
                  />
                </button>
              )}
              <Popover
                open={filterOpen}
                onOpenChange={(open) => {
                  setFilterOpen(open)
                  onFilterOpenChange?.(open)
                }}
              >
                <PopoverTrigger
                  aria-label={`Filter ${label}`}
                  className={cn(
                    'hover:bg-muted hover:text-foreground focus-visible:ring-ring/30 flex size-5 items-center justify-center rounded-sm outline-hidden focus-visible:ring-2',
                    filterActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  <Funnel aria-hidden className="size-2.5 fill-current stroke-[1.5]" />
                </PopoverTrigger>
                {filterOpen ? filterContent(filterOpen) : null}
              </Popover>
            </span>
          </span>
        </span>
      ) : onSort ? (
        <button
          type="button"
          className="hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-sm outline-none focus-visible:ring-2"
          onClick={onSort}
        >
          {label}
          <SortIcon aria-hidden="true" className="size-3" />
        </button>
      ) : label}
    </Th>
  )
}

export function ActionsTh({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th scope="col" className={cn('w-10 px-3 py-2', className)} {...props}>
      <span className="sr-only">Actions</span>
    </th>
  )
}

function ResultAnnouncementContent({ count, hasMore, singular, plural, changeKey }: ResultStatus) {
  const announce = useAnnounce()
  const announcedChangeKey = useRef<string | null>(null)
  const message = count === null
    ? ''
    : `${count}${hasMore ? '+' : ''} ${count === 1 ? singular : plural}`

  useEffect(() => {
    if (!message || announcedChangeKey.current === changeKey) return
    announcedChangeKey.current = changeKey
    announce(message)
  }, [announce, message, changeKey])

  return null
}

function ResultAnnouncement(props: ResultStatus) {
  return (
    <AnnouncerProvider>
      <ResultAnnouncementContent {...props} />
    </AnnouncerProvider>
  )
}

export function DataTable({
  caption,
  headers,
  children,
  empty,
  emptyMessage,
  resultStatus,
  rowCount,
  tableRef,
  tableStyle,
  fixedLayout = false,
}: {
  caption: string
  headers: readonly ReactNode[]
  children: ReactNode
  empty: boolean
  emptyMessage: ReactNode
  resultStatus?: ResultStatus
  rowCount?: number
  tableRef?: Ref<HTMLTableElement>
  tableStyle?: CSSProperties
  fixedLayout?: boolean
}) {
  return (
    <>
      {resultStatus ? <ResultAnnouncement {...resultStatus} /> : null}
      <div data-slot="data-table" className="w-full overflow-x-auto">
        <table
          ref={tableRef}
          style={tableStyle}
          className={cn('border-collapse text-xs', fixedLayout ? 'table-fixed' : 'w-full')}
          aria-rowcount={rowCount}
        >
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr
              aria-rowindex={rowCount === undefined ? undefined : 1}
              className="bg-muted/60 text-left [&>th]:shadow-[inset_0_-1px_0_0] [&>th]:shadow-border"
            >
              {headers}
            </tr>
          </thead>
          <tbody>
            {empty ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="text-muted-foreground px-6 py-8 text-center"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
